/**
 * MissionStageDrawer — reusable contextual drill-down for the Mission Command Bar.
 *
 * Portal popover of the oldest actionable work for a mission stage, anchored to
 * (and visually connected to) the stage's live-count badge via a caret + accent
 * top-border so it reads as ONE interaction, not a floating popup.
 *
 * Endpoint-agnostic: pass `endpoint` (e.g. "/marketing-studio/stage-rows").
 * Auto-closes on outside click + Esc.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Clock, ArrowRight, TrendingUp, Ban, CheckCircle2, Inbox } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";

const INBOX_VIEWS = [
  { key: "oldest", label: "Oldest", Icon: Clock },
  { key: "valuable", label: "Most valuable", Icon: TrendingUp },
  { key: "blocked", label: "Blocked", Icon: Ban },
  { key: "approval", label: "Needs approval", Icon: CheckCircle2 },
];
const EMPTY_BY_VIEW = {
  oldest: "Nothing waiting here.",
  valuable: "No valued items here.",
  blocked: "Nothing blocked. 🎉",
  approval: "Nothing awaiting approval.",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function MissionStageDrawer({ stage, count, accent, anchorRef, endpoint, slug = "mission-stage", onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("oldest");
  const popoverRef = useRef(null);
  const W = 320; // w-80
  const [pos, setPos] = useState({ top: 0, left: 0, caret: W / 2 });

  useLayoutEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const left = Math.max(8, Math.min(window.innerWidth - W - 8, r.left + r.width / 2 - W / 2));
    const caret = Math.max(14, Math.min(W - 14, r.left + r.width / 2 - left));
    setPos({ top: r.bottom + 8, left, caret });
  }, [anchorRef]);

  useEffect(() => {
    setLoading(true);
    api.get(`${endpoint}?stage=${stage}&view=${view}&limit=5`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [stage, endpoint, view]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const open = (r) => { if (r.href) { onClose?.(); navigate(r.href); } };
  const safeAccent = accent || "#6366F1";

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[1000] w-80 rounded-xl border bg-[var(--app-surface)] shadow-2xl p-3"
      style={{ top: pos.top, left: pos.left, borderColor: `${safeAccent}55`, borderTop: `2px solid ${safeAccent}` }}
      onClick={(e) => e.stopPropagation()}
      data-testid={`${slug}-drawer-${stage}`}
      data-stage={stage}
    >
      {/* Caret connector — points up to the stage's count badge. */}
      <span
        aria-hidden="true"
        className="absolute w-2.5 h-2.5 rotate-45"
        style={{
          top: -6, left: pos.caret - 5,
          backgroundColor: "var(--app-surface)",
          borderTop: `2px solid ${safeAccent}`,
          borderLeft: `2px solid ${safeAccent}`,
        }}
      />
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1" style={{ color: safeAccent }}>
          <Inbox className="w-3 h-3" /> {t("Mission Inbox")}
        </p>
        <button
          onClick={onClose}
          className="p-0.5 rounded text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
          aria-label="Close"
          data-testid={`${slug}-drawer-close-${stage}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid={`${slug}-drawer-tabs-${stage}`}>
        {INBOX_VIEWS.map((v) => {
          const activeV = v.key === view;
          return (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors"
              style={activeV
                ? { backgroundColor: `${safeAccent}1f`, color: safeAccent, borderColor: `${safeAccent}66` }
                : { backgroundColor: "transparent", color: "var(--app-text-muted)", borderColor: "var(--app-border)" }}
              data-testid={`${slug}-drawer-tab-${stage}-${v.key}`}
              aria-selected={activeV}
            >
              <v.Icon className="w-2.5 h-2.5" /> {t(v.label)}
            </button>
          );
        })}
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: safeAccent }} />
        </div>
      ) : (rows || []).length === 0 ? (
        <p className="text-xs text-[var(--app-text-muted)] py-3 text-center">{t(EMPTY_BY_VIEW[view] || "No items here right now.")}</p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {rows.map((r, i) => (
              <li key={r.id || i}>
                <button
                  onClick={() => open(r)}
                  className="w-full text-left rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] px-2.5 py-2 transition-colors flex items-center gap-2 hover:border-[var(--app-text-muted)]"
                  data-testid={`${slug}-row-${stage}-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--app-text)] truncate">{r.title}</p>
                    <p className="text-[10px] text-[var(--app-text-muted)] truncate">
                      {r.sub}{r.sub && " · "}{relativeTime(r.updated_at)}
                    </p>
                  </div>
                  <ArrowRight className="w-3 h-3 shrink-0" style={{ color: `${safeAccent}aa` }} />
                </button>
              </li>
            ))}
          </ul>
          {(view === "oldest" || view === "valuable") && count > rows.length && (
            <p className="text-[10px] text-[var(--app-text-muted)] text-center mt-2 pt-2 border-t border-[var(--app-border)]">
              {t("Showing")} {rows.length} {t("of")} <span className="font-bold" style={{ color: safeAccent }}>{count}</span> · {t("open the section to see all")}
            </p>
          )}
        </>
      )}
    </div>,
    document.body
  );
}
